/**
 * Platform Admin — Printer Label Printing.
 *
 * Internal tool for Vanguard Beacon staff to print the identification
 * label that ships on every thermal printer we send to a customer so
 * they know (a) what the device is called and (b) the exact Bluetooth
 * ID to pair in the tenant app.
 *
 * Scope:
 *   - Platform-level page (SUPER_ADMIN_ONLY).
 *   - Reuses the Food Prep platform-wide label dimensions
 *     (`label_width_mm` / `label_height_mm` / `label_dpi`) so the
 *     physical sticker stock is the same.
 *   - Reuses the same TSPL Bluetooth printer flow (`lib/btPrinter.js`)
 *     that tenants use for food labels — same printer, same handshake,
 *     same raster bitmap pipeline.
 *   - Title + Printer ID are ephemeral per print: type → preview →
 *     print → clear. No persistence.
 */
import { useEffect, useRef, useState } from "react";
import { Printer, Bluetooth, RotateCcw, Info } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import {
  connectPrinter, isBluetoothSupported, isPrinterConnected,
  hasRememberedPrinter, reconnectPrinter, printCanvas, getPrinterName,
} from "@/lib/btPrinter";

const MM_PER_IN = 25.4;
const LOGO_SRC = "/vb-logo-print.gif";
const FONT = "'Helvetica Neue', Arial, sans-serif";

// Cache the decoded logo so we don't re-decode it on every preview tick.
let _logoBitmap = null;
async function getLogo() {
  if (_logoBitmap) return _logoBitmap;
  const img = new Image();
  img.src = LOGO_SRC;
  img.crossOrigin = "anonymous";
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
  _logoBitmap = img;
  return img;
}

/**
 * Render the printer-identification label onto `canvas` scaled to the
 * platform's thermal-sticker dimensions. Layout mirrors the reference:
 *   • Upper 55% — Vanguard Beacon logo, centered, max 70% width
 *   • Then title (bold, larger)
 *   • Then printer id (smaller, regular weight)
 */
async function renderPrinterLabel(canvas, { title, printerId }, dims) {
  const { widthMm, heightMm, dpi } = dims;
  const W = Math.round((widthMm * dpi) / MM_PER_IN);
  const H = Math.round((heightMm * dpi) / MM_PER_IN);
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#000000";

  const pad = Math.round(Math.min(W, H) * 0.06);

  // Logo — upper 55%, centered, preserving aspect ratio.
  try {
    const img = await getLogo();
    const logoAreaH = Math.round(H * 0.5);
    const logoAreaW = Math.round(W * 0.7);
    const ratio = img.width / img.height;
    let lw = logoAreaW; let lh = lw / ratio;
    if (lh > logoAreaH) { lh = logoAreaH; lw = lh * ratio; }
    const lx = Math.round((W - lw) / 2);
    const ly = Math.round(pad + (logoAreaH - lh) / 2);
    ctx.drawImage(img, lx, ly, lw, lh);
  } catch { /* logo missing — degrade gracefully, text still renders */ }

  // Title — bold, auto-shrunk to fit width minus padding.
  const titleBand = Math.round(H * 0.6);
  const maxTextW = W - pad * 2;
  let titleSize = Math.round(H * 0.14);
  const titleMin = Math.round(H * 0.085);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = `bold ${titleSize}px ${FONT}`;
  while (titleSize > titleMin && ctx.measureText(title || "").width > maxTextW) {
    titleSize -= 2;
    ctx.font = `bold ${titleSize}px ${FONT}`;
  }
  ctx.fillText(title || "", W / 2, titleBand + titleSize);

  // Printer ID — smaller, same shrink-to-fit treatment.
  let idSize = Math.round(H * 0.1);
  const idMin = Math.round(H * 0.065);
  ctx.font = `500 ${idSize}px ${FONT}`;
  while (idSize > idMin && ctx.measureText(printerId || "").width > maxTextW) {
    idSize -= 1;
    ctx.font = `500 ${idSize}px ${FONT}`;
  }
  ctx.fillText(printerId || "", W / 2, titleBand + titleSize + idSize + 10);
}

export default function PrinterLabel() {
  const { notify } = useDialog();
  const [cfg, setCfg] = useState(null);
  const [title, setTitle] = useState("Thermal Printer Name");
  const [printerId, setPrinterId] = useState("GP-3120TUC_E066_BLE");
  const [copies, setCopies] = useState(1);
  const [busy, setBusy] = useState(false);
  const [printerBusy, setPrinterBusy] = useState(false);
  const [connected, setConnected] = useState(isPrinterConnected());
  const [connectedName, setConnectedName] = useState(getPrinterName());
  const previewRef = useRef(null);

  // Hoist the platform-wide label dimensions from Food Prep config —
  // same thermal stock, same dimensions, no new DB field required.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/food-prep-config");
        setCfg(data);
      } catch (e) { console.debug(e); }
    })();
  }, []);

  // Try a silent reconnect to a previously-paired printer once the
  // page loads, so staff don't see the pairing chooser every time.
  useEffect(() => {
    if (!cfg) return;
    (async () => {
      if (isPrinterConnected() || !hasRememberedPrinter()) return;
      const ok = await reconnectPrinter({
        ble_service_uuid: cfg.ble_service_uuid,
        ble_write_uuid: cfg.ble_write_uuid,
      });
      if (ok) { setConnected(true); setConnectedName(getPrinterName()); }
    })();
  }, [cfg]);

  // Live preview — redraw whenever any input changes.
  useEffect(() => {
    if (!previewRef.current || !cfg) return;
    renderPrinterLabel(previewRef.current, { title, printerId }, {
      widthMm: cfg.label_width_mm || 50,
      heightMm: cfg.label_height_mm || 40,
      dpi: cfg.label_dpi || 203,
    }).catch(() => {});
  }, [title, printerId, cfg]);

  const connect = async () => {
    setPrinterBusy(true);
    try {
      await connectPrinter({
        ble_service_uuid: cfg?.ble_service_uuid,
        ble_write_uuid: cfg?.ble_write_uuid,
      });
      setConnected(true); setConnectedName(getPrinterName());
      await notify({ title: "Connected", message: `Paired with ${getPrinterName() || "printer"}.`, variant: "ok" });
    } catch (e) {
      await notify({ title: "Couldn't connect", message: formatErr(e?.message || e), variant: "err" });
    } finally { setPrinterBusy(false); }
  };

  const doPrint = async () => {
    if (!title.trim() || !printerId.trim()) {
      await notify({ title: "Missing fields", message: "Enter both the label title and the Printer ID before printing.", variant: "err" });
      return;
    }
    setBusy(true);
    try {
      // Ensure the preview canvas is drawn at the latest state before shipping.
      await renderPrinterLabel(previewRef.current, { title, printerId }, {
        widthMm: cfg.label_width_mm || 50,
        heightMm: cfg.label_height_mm || 40,
        dpi: cfg.label_dpi || 203,
      });
      // Auto-connect if the first print of the session hasn't paired yet.
      if (!isPrinterConnected()) {
        try { await connectPrinter({
          ble_service_uuid: cfg?.ble_service_uuid,
          ble_write_uuid: cfg?.ble_write_uuid,
        }); setConnected(true); setConnectedName(getPrinterName()); }
        catch (e) {
          await notify({ title: "Printer not paired", message: formatErr(e?.message || e), variant: "err" });
          return;
        }
      }
      await printCanvas(previewRef.current, {
        label_width_mm: cfg.label_width_mm,
        label_height_mm: cfg.label_height_mm,
        printer_command: cfg.printer_command || "tspl",
        printer_gap_mm: cfg.printer_gap_mm ?? 2,
      }, copies);
      await notify({ title: "Sent to printer", message: `${copies} label${copies === 1 ? "" : "s"} sent.`, variant: "ok" });
      // Ephemeral spec — clear after a successful print so the next unit starts fresh.
      setTitle("Thermal Printer Name");
      setPrinterId("");
      setCopies(1);
    } catch (e) {
      await notify({ title: "Print failed", message: formatErr(e?.message || e), variant: "err" });
    } finally { setBusy(false); }
  };

  const btSupported = isBluetoothSupported();
  const widthMm = cfg?.label_width_mm || 50;
  const heightMm = cfg?.label_height_mm || 40;

  return (
    <div className="vb-fade-in" data-testid="printer-label-page" style={{ maxWidth: 1000 }}>
      <header className="mb-4">
        <h1 className="vb-heading text-3xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
          <Printer size={24} className="inline-block mr-2 -mt-1" /> Printer Labels
        </h1>
        <p className="text-sm opacity-70 m-0">
          Print the identification label that ships on every thermal printer. Uses the same label stock + Bluetooth printer as the Food Prep tenant flow.
        </p>
      </header>

      <div className="vb-card p-4 mb-4 flex items-start gap-2" data-testid="printer-label-note">
        <Info size={16} className="mt-0.5 shrink-0" style={{ color: "var(--vb-text-2)" }} />
        <span className="text-sm opacity-80">
          Label dimensions come from <strong>Food Prep → Print Settings</strong> ({widthMm}×{heightMm} mm). Title and Printer ID below are per-print — they clear after each successful print.
        </span>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-[minmax(0,360px)_1fr]">
        {/* Left column — inputs + print action */}
        <div className="vb-card p-5 h-fit" data-testid="printer-label-form">
          <h3 className="vb-heading text-lg mt-0 mb-3">Label content</h3>
          <label className="block mb-3 text-sm">
            <span className="block mb-1 font-medium">Label title</span>
            <input className="vb-input" value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Thermal Printer Name"
              data-testid="printer-label-title" />
          </label>
          <label className="block mb-3 text-sm">
            <span className="block mb-1 font-medium">Printer ID</span>
            <input className="vb-input" value={printerId}
              onChange={(e) => setPrinterId(e.target.value)}
              placeholder="e.g. GP-3120TUC_E066_BLE"
              style={{ fontFamily: "monospace" }}
              data-testid="printer-label-id" />
          </label>
          <label className="block mb-4 text-sm">
            <span className="block mb-1 font-medium">Copies</span>
            <input className="vb-input" type="number" min="1" max="100" value={copies}
              onChange={(e) => setCopies(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
              style={{ maxWidth: 120 }}
              data-testid="printer-label-copies" />
          </label>

          <div className="rounded-lg p-3 mb-4 flex items-start gap-2" style={{ background: "var(--vb-bg)" }}>
            <Bluetooth size={15} className="mt-0.5 shrink-0" style={{ color: "var(--vb-text-2)" }} />
            <div className="text-xs opacity-80">
              {!btSupported && (
                <span className="text-red-600">Web Bluetooth not supported in this browser — use Chrome on Android or desktop Chrome.</span>
              )}
              {btSupported && !connected && (
                <>Not paired yet. The first print of the session will show the Bluetooth chooser — pick the printer once and it reconnects after that.</>
              )}
              {btSupported && connected && (
                <>Paired with <strong>{connectedName || "printer"}</strong>.</>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button className="vb-btn flex-1" disabled={busy || !btSupported}
              onClick={doPrint} data-testid="printer-label-print">
              <Printer size={16} /> {busy ? "Printing…" : `Print ${copies} label${copies === 1 ? "" : "s"}`}
            </button>
            {btSupported && !connected && (
              <button className="vb-btn ghost shrink-0" disabled={printerBusy}
                onClick={connect} data-testid="printer-label-pair">
                <Bluetooth size={14} /> {printerBusy ? "Pairing…" : "Pair"}
              </button>
            )}
            <button className="vb-btn ghost shrink-0"
              onClick={() => { setTitle("Thermal Printer Name"); setPrinterId(""); setCopies(1); }}
              data-testid="printer-label-reset" title="Clear fields">
              <RotateCcw size={14} />
            </button>
          </div>
        </div>

        {/* Right column — live preview, scaled 1:1 at a readable zoom */}
        <div className="vb-card p-5" data-testid="printer-label-preview-card">
          <h3 className="vb-heading text-lg mt-0 mb-3">
            Live preview <span className="text-xs opacity-60 font-normal">({widthMm}×{heightMm} mm)</span>
          </h3>
          <div style={{ display: "flex", justifyContent: "center", background: "var(--vb-bg)", padding: 16, borderRadius: 8 }}>
            <div style={{
              width: `${widthMm * 4}px`, height: `${heightMm * 4}px`,
              background: "#fff", border: "1px solid #d4c7b7", borderRadius: 4,
              overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}>
              <canvas ref={previewRef}
                style={{ width: "100%", height: "100%", display: "block" }}
                data-testid="printer-label-canvas" />
            </div>
          </div>
          <p className="text-xs opacity-60 mt-3 m-0 text-center">
            Preview rendered at the configured sticker size × 4 (screen pixels per mm) — the printed output is the same bitmap at {cfg?.label_dpi || 203} dpi.
          </p>
        </div>
      </div>
    </div>
  );
}
